import { useMemo, useRef, useState, useEffect } from "react";
import { ProcessingHeader } from "./ProcessingHeader";
import { ProcessingItem } from "./ProcessingItem";
import { CompletionCelebration } from "./CompletionCelebration";
import type { ProcessingItemData } from "./types";

interface ProcessingViewProps {
  items: ProcessingItemData[];
  onRetry: (itemId: string) => void;
  onRetryAllFailed: () => void;
  onUploadMore: () => void;
}

export function ProcessingView({
  items,
  onRetry,
  onRetryAllFailed,
  onUploadMore,
}: ProcessingViewProps) {
  const [startTime] = useState(Date.now());
  const [elapsed, setElapsed] = useState("00:00");
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const counts = useMemo(() => {
    let uploading = 0;
    let extracting = 0;
    let complete = 0;
    let failed = 0;

    for (const item of items) {
      switch (item.status) {
        case "uploading":
          uploading++;
          break;
        case "extracting":
          extracting++;
          break;
        case "extracted":
          complete++;
          break;
        case "failed":
        case "error":
          failed++;
          break;
      }
    }

    return { uploading, extracting, complete, failed, total: items.length };
  }, [items]);

  const isComplete =
    counts.uploading === 0 &&
    counts.extracting === 0 &&
    counts.complete + counts.failed === counts.total &&
    counts.total > 0;

  // Track elapsed for completion summary
  useEffect(() => {
    timerRef.current = setInterval(() => {
      const diff = Math.floor((Date.now() - startTime) / 1000);
      const mins = String(Math.floor(diff / 60)).padStart(2, "0");
      const secs = String(diff % 60).padStart(2, "0");
      setElapsed(`${mins}:${secs}`);
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startTime]);

  useEffect(() => {
    if (isComplete && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [isComplete]);

  // Only show processing items (not staged)
  const processingItems = items.filter((i) => i.status !== "staged");

  return (
    <div className="space-y-4">
      <ProcessingHeader counts={counts} elapsed={elapsed} isComplete={isComplete} />

      {/* Failed banner */}
      {counts.failed > 3 && !isComplete && (
        <div
          className="flex items-center justify-between rounded-ds-sm border border-bad/30 px-4 py-2"
          style={{ background: "color-mix(in oklab, var(--tr-bad) 6%, transparent)" }}
        >
          <span className="text-sm text-bad">
            {counts.failed} extractions failed
          </span>
          <button
            onClick={onRetryAllFailed}
            className="text-xs font-medium text-bad transition-colors hover:opacity-80"
          >
            Retry all failed
          </button>
        </div>
      )}

      {/* File list */}
      <div className="space-y-2">
        {processingItems.map((item, i) => (
          <ProcessingItem
            key={item.id}
            item={item}
            index={i}
            onRetry={() => onRetry(item.id)}
          />
        ))}
      </div>

      {/* Completion celebration */}
      {isComplete && (
        <CompletionCelebration
          totalComplete={counts.complete}
          totalFailed={counts.failed}
          elapsed={elapsed}
          onRetryAllFailed={onRetryAllFailed}
          onUploadMore={onUploadMore}
        />
      )}
    </div>
  );
}
